import { KcError } from './KeycloakError'
import type { AuthState } from '../types/AuthState'
import type {
  KeycloakHostedLoginResponseType,
  KeycloakWebfingerFederatedResponseType,
  KeycloakWebfingerResponseType,
} from '../types/KeycloakResponse'
import { getAccountLifecycleDomain } from './getForgotPasswordAbsoluteLink'

export type BaseType = {
  authState: AuthState
  jwt: string
  email?: string
}

export type WebfingerDiscoveryType = Required<BaseType>

export type HostedLoginType = WebfingerDiscoveryType & {
  password: string
}

export type DiscoveryType = BaseType & {
  mode: 'webfinger' | 'login-firm-member'
}

export type FirmDiscoveryType = Omit<WebfingerDiscoveryType, 'email'>

export type McKIDLoginFlow = 'hosted' | 'federated'

type LoginUiHeadersInput = {
  authState: AuthState
  jwt: string
}

export const loginUiHeaders = ({ authState, jwt }: LoginUiHeadersInput): HeadersInit => ({
  Authorization: `Bearer ${jwt}`,
  'Cache-Control': 'no-cache, no-store, max-age=0',
  'Content-Type': 'application/json',
  'x-mid-session-id': authState.authSessionId,
  'x-mid-tab-id': authState.tabId,
})

export const hostedLogin = async ({ authState, jwt, email, password }: HostedLoginType): Promise<void> => {
  const response = await fetch(`${authState.iss}/login-ui/login`, {
    method: 'POST',
    headers: loginUiHeaders({ authState, jwt }),
    body: JSON.stringify({ username: email, password }),
  })
  const kcResp: KeycloakHostedLoginResponseType = await response.json()

  if (kcResp.status === 'redirect') {
    console.log('login endpoint says redirect to', kcResp.location)

    try {
      const locationUrlObject = new URL(kcResp.location)
      const { host, searchParams } = locationUrlObject
      const { host: alDomainHost } = new URL(getAccountLifecycleDomain())
      if (host === alDomainHost) {
        // redirect to Account Lifecycle UI
        searchParams.set('login-ui-session', jwt)
        const newLocation = locationUrlObject.toString()
        console.log('redirect to Account Lifecycle UI', newLocation)
        window.location.assign(newLocation)
        return
      }
    } catch (error) {
      // if an error happens, the previous flow remains untouched
      console.error(error)
    }
    window.location.assign(kcResp.location)
  } else {
    throw new KcError(kcResp as Required<KeycloakHostedLoginResponseType>, 'login endpoint')
  }
}

const discovery = async ({ authState, jwt, email, mode }: DiscoveryType): Promise<McKIDLoginFlow> => {
  const url = new URL(`${authState.iss}/login-ui/${mode}`)
  if (email) url.searchParams.set('resource', email)
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: loginUiHeaders({ authState, jwt }),
  })
  const kcResp: KeycloakWebfingerResponseType = await response.json()

  if ('status' in kcResp && kcResp.status !== 'redirect' && kcResp.message) {
    throw new KcError(kcResp as Required<KeycloakWebfingerFederatedResponseType>, 'webfinger endpoint')
  }

  // Goes to federated flow
  if ('status' in kcResp && kcResp.status === 'redirect') {
    console.log(mode, 'endpoint says redirect to', kcResp.location)
    window.location.assign(kcResp.location)
    return 'federated'
  }

  // Goes to hosted flow
  // if ('subject' in kcResp) {}
  return 'hosted'
}

export const webfingerDiscovery = ({ authState, jwt, email }: WebfingerDiscoveryType): Promise<McKIDLoginFlow> => {
  return discovery({ authState, jwt, email, mode: 'webfinger' })
}

export const loginAsFirmMember = async ({ authState, jwt }: FirmDiscoveryType): Promise<McKIDLoginFlow> => {
  return discovery({ authState, jwt, mode: 'login-firm-member' })
}
